/**
 * Form-tracking helper for the /get-found qualifying questionnaire.
 * Fires a CRM tracking event (fire-and-forget) so the lead is captured
 * before the visitor is sent to checkout. Never blocks the redirect.
 *
 * Two submission paths:
 *  - trackQualifyingSubmission: budget = yes, full flow. Categorized as
 *    "HOT LEAD - PLAYBOOK" if they want 1:1 help, otherwise "newsletter".
 *  - trackNewsletterSignup: budget = no, name + email only, saved to the
 *    newsletter list.
 */

type StandardTrackingFieldKey = string;
type RegisteredCustomFieldId = string;
type TrackingCustomField = { value?: unknown; label: string };
type TrackingFileField = { file?: File; label: string };
type TrackingImageDataField = { dataUrl?: string; label: string };

const TRACKING_ID = "tk_947f7a50a2724eed976a1cb0e2679478";
const LOCATION_ID = "rizeckkMPBYZT9pK9UjX";
const PROJECT_ID = "1790213736784009104";

const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<StandardTrackingFieldKey, unknown>;
    formLabels: Record<StandardTrackingFieldKey, string>;
  },
  options: {
    customFields?: Record<RegisteredCustomFieldId, TrackingCustomField>;
    fileFields?: Record<RegisteredCustomFieldId, TrackingFileField>;
    imageDataFields?: Record<RegisteredCustomFieldId, TrackingImageDataField>;
  } = {},
) => {
  const { customFields = {}, fileFields = {}, imageDataFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };
  const body = new FormData();

  for (const [key, field] of Object.entries(customFields)) {
    if (field.value === undefined) continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(imageDataFields)) {
    const dataUrl = field.dataUrl;
    if (!dataUrl) continue;
    if (!dataUrl.startsWith("data:image/")) {
      throw new Error("Image data field must be a data:image/* base64 string");
    }
    eventPayload.formData[key] = dataUrl;
    eventPayload.formLabels[key] = field.label;
  }

  for (const [key, field] of Object.entries(fileFields)) {
    const file = field.file;
    if (!file) continue;
    if (file.size > 50 * 1024 * 1024) {
      throw new Error("File must be 50 MB or smaller");
    }
    eventPayload.formData[key] = {
      filename: file.name,
      size: file.size,
      type: file.type || "application/octet-stream",
    };
    eventPayload.formLabels[key] = field.label;
    body.append(key, file, file.name);
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  body.append("event", JSON.stringify(eventPayload));

  fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: { version: "2021-07-28" },
    body,
  }).catch(() => {});
};

export interface QualifyingFormValues {
  hearAbout: string;
  goal: string;
  budget: string;
  firstName: string;
  lastName: string;
  email: string;
  businessName: string;
  workWithUs: string;
}

export interface NewsletterValues {
  hearAbout: string;
  goal: string;
  firstName: string;
  lastName: string;
  email: string;
}

function basePayload(formName: string) {
  return {
    type: "external form_submission",
    timestamp: Date.now(),
    formId: "get-found-qualifying",
    url: window.location.href,
    title: document.title,
    path: window.location.pathname,
    userAgent: navigator.userAgent,
    trackingId: TRACKING_ID,
    locationId: LOCATION_ID,
    projectId: PROJECT_ID,
    sessionId: crypto.randomUUID(),
    properties: {
      deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
        ? "mobile"
        : "desktop",
      source: "ai_studio",
      projectId: PROJECT_ID,
      formName,
    },
  };
}

/** Full qualifying flow (budget = yes). Categorizes HOT LEAD vs newsletter. */
export function trackQualifyingSubmission(values: QualifyingFormValues) {
  const leadCategory =
    values.workWithUs === "yes" ? "HOT LEAD - PLAYBOOK" : "newsletter";

  const trackingPayload = {
    ...basePayload("Get Found Qualifying Questionnaire"),
    formData: {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      business_name: values.businessName,
      hear_about: values.hearAbout,
      goal: values.goal,
      budget: values.budget,
      work_with_us: values.workWithUs,
      lead_category: leadCategory,
    },
    formLabels: {
      first_name: "First Name",
      last_name: "Last Name",
      email: "Email",
      business_name: "Business Name",
      hear_about: "How They Heard About Us",
      goal: "Goal With The Guide",
      budget: "$47 Within Budget",
      work_with_us: "Wants 1:1 Help",
      lead_category: "Lead Category",
    },
  };

  postTrackingEvent(trackingPayload, {
    customFields: {
      fmA6gMQ7rGx9SgPadQDW: {
        value: values.businessName,
        label: "Business Name",
      },
      PSgX1kFjqaU41Ry6Y5ET: {
        value: leadCategory,
        label: "Lead Category",
      },
    },
  });
}

/** No-budget path: name + email saved to the newsletter list. */
export function trackNewsletterSignup(values: NewsletterValues) {
  const trackingPayload = {
    ...basePayload("Get Found Newsletter Signup"),
    formData: {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      hear_about: values.hearAbout,
      goal: values.goal,
      budget: "no",
      lead_category: "newsletter",
    },
    formLabels: {
      first_name: "First Name",
      last_name: "Last Name",
      email: "Email",
      hear_about: "How They Heard About Us",
      goal: "Goal With The Guide",
      budget: "$47 Within Budget",
      lead_category: "Lead Category",
    },
  };

  postTrackingEvent(trackingPayload, {
    customFields: {
      PSgX1kFjqaU41Ry6Y5ET: {
        value: "newsletter",
        label: "Lead Category",
      },
    },
  });
}
